import type { CSSProperties } from 'react';

/**
 * Единый тонкий line-icon набор (Антверпен): inline SVG, рисуется stroke'ом
 * в currentColor — красится цветом текста, не зависит от системных эмодзи.
 */

export type IconName =
  | 'home'
  | 'plus'
  | 'list'
  | 'trend'
  | 'map'
  | 'gear'
  | 'inbox'
  | 'sync'
  | 'leaf'      // стрик записей
  | 'coin'      // монеты сезона
  | 'snow'      // заморозка
  | 'feather'   // награда / мотив
  | 'target'    // квест
  | 'skull'     // босс месяца
  | 'shield'    // цель месяца / подушка
  | 'chest'     // сундук
  | 'arrowDown'
  | 'arrowRight'
  | 'check'
  | 'close'
  // --- категорийные ---
  | 'food'
  | 'coffee'
  | 'transport'
  | 'alcohol'
  | 'health'
  | 'therapy'
  | 'clothes'
  | 'travel'
  | 'gift'
  | 'subscription'
  | 'transfer'
  | 'dumbbell'
  | 'wallet'
  | 'laptop'
  | 'book'
  | 'broom'
  | 'tag'
  | 'dots'
  | 'star'
  | 'trash'
  | 'chevronLeft'
  | 'grip'
  | 'chevronUp'
  | 'chevronDown';

const PATHS: Record<IconName, string> = {
  home: 'M3 10l9-7 9 7M5 9v11h14V9',
  plus: 'M12 5v14M5 12h14',
  list: 'M8 6h12M8 12h12M8 18h12M3.5 6h.01M3.5 12h.01M3.5 18h.01',
  trend: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  map: 'M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14',
  gear: 'M12 8.8a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2',
  inbox: 'M4 5h16v14H4zM4 13h5a3 3 0 006 0h5',
  sync: 'M21 12a9 9 0 11-3-6.7M21 4v4h-4',
  leaf: 'M11 21C5 17 4 9 12 4c2 7-1 13-1 17zM11 21c0-5 1-9 4-12',
  coin: 'M12 4a8 8 0 100 16 8 8 0 000-16M12 8v8M9.5 10.2c0-1.3 1.1-2 2.5-2s2.5.7 2.5 1.9-1.1 1.6-2.5 1.9-2.5.6-2.5 1.9 1.1 2 2.5 2 2.5-.7 2.5-2',
  snow: 'M12 2v20M4 6l16 12M20 6L4 18M3 12h18',
  feather: 'M20 4C13 4 7 9 5 16l-1 4 4-1c7-2 12-8 12-15zM16 8l-8 8M14 9h-3M13 12h-3',
  target: 'M12 4a8 8 0 100 16 8 8 0 000-16M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7',
  skull: 'M12 3c-5 0-8 3-8 8 0 3 2 4 2 6h3v2h6v-2h3c0-2 2-3 2-6 0-5-3-8-8-8M9 12.5a1.4 1.4 0 100-.1M15 12.5a1.4 1.4 0 100-.1',
  shield: 'M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z',
  chest: 'M4 9l1-3h14l1 3M4 9v10h16V9M4 9h16M12 9v3M10 12h4',
  arrowDown: 'M12 5v14M6 13l6 6 6-6',
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  check: 'M5 12l5 5L20 6',
  close: 'M6 6l12 12M18 6L6 18',
  // --- категорийные (line, 24-грид) ---
  food: 'M6 3v7a2 2 0 004 0V3M8 10v11M16 3c-1.6 0-2.6 2-2.6 5s1 4 2.6 4v9',
  coffee: 'M5 8h11v6a4 4 0 01-4 4H9a4 4 0 01-4-4zM16 9h2.5a2.5 2.5 0 010 5H16M8 3v2M11 3v2',
  transport: 'M6 4h12v10H6zM6 14l-1.5 5M18 14l1.5 5M9 19h6M8.5 10h7M9 4v6',
  alcohol: 'M7 3h10l-1.4 7.5a4 4 0 01-7.2 0zM12 14.5V20M9 20h6',
  health: 'M3 12h4l2-6 4 13 2-7h6',
  therapy: 'M12 20s-7-4.6-7-9.2A3.6 3.6 0 0112 9a3.6 3.6 0 017 1.8C19 15.4 12 20 12 20z',
  clothes: 'M8.5 4L4 7l2 3 2.5-1.3V20h7V8.7L18 10l2-3-4.5-3-1.5 2h-4z',
  travel: 'M21 16l-7.5-2.8V6a1.5 1.5 0 00-3 0v7.2L3 16v1.8l7.5-1.8v3L8.5 20.4V22l3.5-1 3.5 1v-1.6L13.5 19v-3l7.5 1.8z',
  gift: 'M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7C10.8 4 6.5 4.2 7.2 7M12 7c1.2-3 5.5-2.8 4.8 0',
  subscription: 'M4.5 9A8 8 0 0118 5.3L20 7M19.5 15A8 8 0 016 18.7L4 17M20 3v4h-4M4 21v-4h4',
  transfer: 'M7 4L3.5 7.5 7 11M3.5 7.5H16M17 13l3.5 3.5L17 20M20.5 16.5H8',
  dumbbell: 'M6.5 8v8M4 9.5v5M17.5 8v8M20 9.5v5M6.5 12h11',
  wallet: 'M4 7h13a2 2 0 012 2v8a2 2 0 01-2 2H4zM4 7V5.5A1.5 1.5 0 015.5 4H15M16.5 13h.01',
  laptop: 'M5 5.5h14V15H5zM3 19h18l-1.3-2.5H4.3z',
  book: 'M5 4.5h10.5a2 2 0 012 2V20H7a2 2 0 01-2-2zM5 18a2 2 0 012-2h10.5',
  broom: 'M15 3l6 6M13.5 5.5l5 5-7.5 7-5.5 1.3 1.3-5.5zM5.5 18.5C4 20 4 21 3.5 21.5',
  tag: 'M4 4h7.5L20 12.5 12.5 20 4 11.5zM8 8h.01',
  dots: 'M5 12h.01M12 12h.01M19 12h.01',
  star: 'M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 17l-5.3 2.7 1.1-5.9-4.3-4.1 5.9-.8z',
  trash: 'M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13M10 11v6M14 11v6',
  chevronLeft: 'M15 5l-7 7 7 7',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  chevronUp: 'M6 15l6-6 6 6',
  chevronDown: 'M6 9l6 6 6-6',
};

/** Иконка по названию категории (рус). Fallback по эмодзи, затем 'dots'. */
const BY_NAME: Record<string, IconName> = {
  'Еда / продукты': 'food',
  'Кафе и рестораны': 'food',
  'Кофе': 'coffee',
  'Транспорт': 'transport',
  'Алкоголь': 'alcohol',
  'Ашкудишки': 'star',
  'Парная терапия': 'therapy',
  'Психотерапевт': 'therapy',
  'Здоровье': 'health',
  'Хотелки (хобби/спорт)': 'dumbbell',
  'Одежда': 'clothes',
  'Путешествия': 'travel',
  'Подписки': 'subscription',
  'Подарки': 'gift',
  'Крупные покупки': 'tag',
  'Прочее': 'dots',
  'Перевод партнёру': 'transfer',
  'Аренда/счета': 'home',
  'Быт': 'broom',
  'Зарплата': 'wallet',
  'Репетиторство': 'book',
  'Фриланс': 'laptop',
  'Прочий доход': 'coin',
};

const BY_EMOJI: Record<string, IconName> = {
  '🍽': 'food', '🍔': 'food', '🍕': 'food', '☕': 'coffee', '🚕': 'transport', '🚇': 'transport',
  '🍷': 'alcohol', '🍺': 'alcohol', '❤️': 'therapy', '🧠': 'therapy', '💊': 'health', '🏥': 'health',
  '👕': 'clothes', '✈️': 'travel', '🎁': 'gift', '🔁': 'subscription', '🏠': 'home', '🏡': 'home',
  '💪': 'dumbbell', '💰': 'wallet', '💻': 'laptop', '📚': 'book', '🎯': 'target', '⭐': 'star',
};

export const PICKABLE_ICONS: IconName[] = [
  'food', 'coffee', 'transport', 'alcohol', 'health', 'therapy', 'clothes',
  'travel', 'gift', 'subscription', 'transfer', 'dumbbell', 'home', 'broom',
  'tag', 'wallet', 'laptop', 'book', 'coin', 'star', 'dots',
];

export function categoryIconName(category: { name: string; icon?: string }): IconName {
  const ic = (category.icon ?? '').trim();
  // Явный валидный icon из БД — приоритет выше BY_NAME.
  if (ic && ic in PATHS) return ic as IconName;
  // Легаси: эмодзи в поле icon.
  if (ic && BY_EMOJI[ic]) return BY_EMOJI[ic];
  // Fallback по имени категории для пустого/невалидного icon.
  if (BY_NAME[category.name]) return BY_NAME[category.name];
  return 'dots';
}

/** Иконка группы категорий (для трендов). */
const GROUP_ICON: Record<string, IconName> = {
  'Еда': 'food',
  'Дом': 'home',
  'Терапия и здоровье': 'health',
  'Личное': 'star',
  'Близкие': 'gift',
  'Редкое': 'travel',
};

export function groupIconName(group: string): IconName {
  return GROUP_ICON[group] ?? 'dots';
}

interface Props {
  name: IconName;
  size?: number;
  stroke?: number;
  color?: string;
  style?: CSSProperties;
  /** Вертикальное выравнивание со строчным текстом. */
  inline?: boolean;
}

export function Icon({ name, size = 20, stroke = 1.3, color = 'currentColor', style, inline }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...(inline ? { verticalAlign: '-0.15em' } : {}), ...style }}
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
